import type { PlannerHeatRequest, PlannerHeatView } from './heat_view.js';
import { type RouteEndpoints, swapEndpointsOnDuplicate } from './leg_state.js';
import { addMonths, useBinarySheet, windowMonths } from './sheet_months.js';
import { binaryHeatMeta, dayToneOf, tierDayState } from './heat_tone.js';
import { resolvePolicy } from './travelers_field.js';
import type { PlannerGuide } from './guided_fill.js';
import { useEffect, useRef, useState } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import type { PlannerFormProps } from './planner_form_model.js';
import type { PlannerState } from './planner_state.js';

/*
 * O modo BINÁRIO do planner (ida e volta, somente ida): a rota, o heat da
 * janela, o pager do calendário e o eco da busca. Saiu de `planner_form.tsx`
 * pelo teto de linhas dele.
 */

function useRoutePreview({
  state,
  heat,
  heatLoading,
  readOnly,
  autoFetchHeat,
  fetchHeat,
  onSubmit,
  routeOk,
  analyzed,
  echoFields,
  afterPick,
}: {
  state: PlannerState;
  heat: PlannerHeatView;
  heatLoading: boolean;
  readOnly: boolean;
  autoFetchHeat: boolean;
  fetchHeat: PlannerFormProps['fetchHeat'];
  onSubmit: PlannerFormProps['onSubmit'];
  routeOk: boolean;
  analyzed: number;
  echoFields: () => Omit<PlannerHeatRequest, 'origin' | 'destination' | 'fromMonth' | 'count'>;
  /** Uma ponta acabou de ser ESCOLHIDA (pick no De/Para; `swapped` quando a
   *  escolha inverteu as pontas) — o gancho do preenchimento guiado. */
  afterPick: PlannerGuide['afterRoutePick'];
}) {
  const {
    origin,
    setOrigin,
    destination,
    setDestination,
    originLabel,
    setOriginLabel,
    destinationLabel,
    setDestinationLabel,
    out,
    ret,
  } = state;
  const autoHeatRequested = useRef(false);
  // `meta` só carrega quando é o self-heal AUTOMÁTICO (ver o effect abaixo). Os
  // gestos explícitos (pick/swap/pager) chamam `preview` sem 4º argumento — o
  // spread condicional mantém a chamada a `fetchHeat` com UM argumento só,
  // igual a antes do seam existir; é o `FlightPlannerCard` que assume 'user'
  // como default na ausência de `meta`.
  const preview = (
    nextOrigin: string,
    nextDestination: string,
    fromMonth = heat.window.fromMonth,
    meta?: { trigger: 'user' | 'auto' },
    // Datas EXPLÍCITAS para o gesto que acabou de escolhê-las (pick da ida,
    // #2106): o `setOut` ainda não commitou quando o preview dispara, então o
    // par do closure estaria um gesto atrasado. Presente, substitui o closure
    // por inteiro — meio a meio misturaria duas gerações do par.
    dates?: { departDate?: string; returnDate?: string },
  ) => {
    if (!nextOrigin.trim() || !nextDestination.trim()) return;
    // Rota impossível (CC-5): o aviso inline já cobre o CTA; aqui se corta o read
    // de heat, que iria ao provider e voltaria como falha genérica.
    if (nextOrigin.trim() === nextDestination.trim()) return;
    autoHeatRequested.current = true;
    const departDate = dates ? dates.departDate : out;
    const returnDate = dates ? dates.returnDate : ret;
    fetchHeat(
      {
        origin: nextOrigin.trim(),
        destination: nextDestination.trim(),
        fromMonth,
        count: heat.window.count,
        ...(departDate ? { departDate } : {}),
        ...(returnDate ? { returnDate } : {}),
        ...echoFields(),
      },
      ...(meta ? [meta] : []),
    );
  };
  // O self-heal lê a janela e o `preview` de agora; quem o arma são só as deps
  // abaixo — a rota entra como argumento (uma ida nova sozinha não muda o gate).
  const autoPreview = useStableCallback((nextOrigin: string, nextDestination: string) => {
    preview(nextOrigin, nextDestination, heat.window.fromMonth, { trigger: 'auto' });
  });
  useEffect(() => {
    if (autoHeatRequested.current || readOnly || heatLoading || !autoFetchHeat) return;
    if (!routeOk || analyzed > 0) return;
    autoPreview(origin, destination);
  }, [readOnly, heatLoading, autoFetchHeat, routeOk, analyzed, origin, destination, autoPreview]);
  /* As quatro pontas viram um estado só na hora de aplicar: a inversão troca as
     duas de uma vez, e setters espalhados fariam o preview enxergar meia
     troca. */
  const applyRoute = (next: RouteEndpoints) => {
    setOrigin(next.origin);
    setOriginLabel(next.originLabel);
    setDestination(next.destination);
    setDestinationLabel(next.destinationLabel);
  };
  const liveRoute = () => ({ origin, originLabel, destination, destinationLabel });
  const pickOrigin = (value: string, label: string) => {
    const swapped = swapEndpointsOnDuplicate(
      'origin',
      { iata: value, label: label || undefined },
      liveRoute(),
    );
    const next: RouteEndpoints = swapped ?? {
      origin: value,
      originLabel: label || undefined,
      destination,
      destinationLabel,
    };
    applyRoute(next);
    // `preview` já corta rota incompleta e rota impossível; aqui só se evita
    // repetir o heat que a janela em cena já descreve.
    if (next.origin !== heat.route.origin || next.destination !== heat.route.destination) {
      preview(next.origin, next.destination);
    }
    afterPick(next, swapped !== null);
  };
  const pickDestination = (value: string, label: string) => {
    const swapped = swapEndpointsOnDuplicate(
      'destination',
      { iata: value, label: label || undefined },
      liveRoute(),
    );
    const next: RouteEndpoints = swapped ?? {
      origin,
      originLabel,
      destination: value,
      destinationLabel: label || undefined,
    };
    applyRoute(next);
    if (next.origin !== heat.route.origin || next.destination !== heat.route.destination) {
      preview(next.origin, next.destination);
    }
    afterPick(next, swapped !== null);
  };
  const swap = () => {
    setOrigin(destination);
    setDestination(origin);
    setOriginLabel(destinationLabel);
    setDestinationLabel(originLabel);
    if (routeOk) preview(destination, origin);
  };
  const search = (outboundDate: string, returnDate?: string) => {
    onSubmit({
      action: 'search',
      origin: origin.trim(),
      destination: destination.trim(),
      outboundDate,
      ...(returnDate ? { returnDate } : {}),
      ...echoFields(),
    });
  };
  return { preview, pickOrigin, pickDestination, swap, search };
}

function binaryFlags(state: PlannerState, initial: PlannerFormProps['initial']) {
  const showTabs = initial.tripType != null;
  const oneWayTab = showTabs && state.activeTripType === 'one_way';
  const routeOk = Boolean(state.origin.trim()) && Boolean(state.destination.trim());
  // Rota impossível é erro de INPUT: sem o guard, origem==destino buscava no vendor
  // e voltava como falha genérica com retry inútil (CC-5). O aviso inline é da
  // RouteRowView; aqui só se trava o CTA em todos os hosts do form (card, faixa).
  const sameEndpoints = routeOk && state.origin.trim() === state.destination.trim();
  const ready =
    routeOk &&
    !sameEndpoints &&
    Boolean(state.out) &&
    (!showTabs || state.activeTripType !== 'round_trip' || Boolean(state.ret));
  // A fase "escolhendo a VOLTA" (#2106): ida marcada, volta em aberto, e a aba não
  // é só-ida. É ela que sobe o piso do pescável para a própria ida e apaga o calor
  // de quem ficou abaixo dele.
  const pickingReturn = !oneWayTab && Boolean(state.out) && !state.ret;
  const echoPolicyTravelerId = state.travelers.length
    ? resolvePolicy(state.travelers, state.policyTravelerId, initial.requesterId)
    : undefined;
  return {
    showTabs,
    oneWayTab,
    routeOk,
    sameEndpoints,
    ready,
    pickingReturn,
    echoPolicyTravelerId,
  };
}

/**
 * O que o heat em mãos diz sobre a grade em cena.
 *
 * **A rota é o portão dos tiers, e não o `loading` (#2330).** Enquanto a grade
 * inteira sumia durante a leitura, era o shimmer que impedia de pintar o preço
 * de uma rota sobre outra — de graça, e sem ninguém ter escrito a regra. Com a
 * grade viva o portão precisa ser explícito: o heat descreve a rota que ele
 * mesmo carrega (`heat.route`), então tier só entra quando ela é a rota do
 * formulário. Trocar GRU→REC por GRU→SSA zera a cor na hora; virar a PÁGINA
 * (mesma rota, outra janela) mantém pintado o que já se sabe, e os meses fora da
 * janela buscada ficam neutros — que é a leitura honesta de "ainda não sei".
 */
function useCalendarPager({
  heat,
  routeOk,
  origin,
  destination,
  preview,
  monthsVisible,
  floorIso,
}: {
  heat: PlannerHeatView;
  routeOk: boolean;
  origin: string;
  destination: string;
  preview: (origin: string, destination: string, fromMonth?: string) => void;
  monthsVisible: number;
  /** Piso do pescável — hoje, ou a IDA enquanto se escolhe a volta (#2106). O
   *  pager não abre mês anterior ao dele. */
  floorIso: string;
}) {
  const [viewIdx, setViewIdx] = useState(0);
  const months = windowMonths(heat.window);
  const monthFloor = floorIso.slice(0, 7);
  const month = months[viewIdx] ?? heat.window.fromMonth;
  /** A seta do calendário pediu `next`: dentro da janela é só virar a página;
   *  fora dela é pedir ao host a janela vizinha, re-ancorada — a mesma leitura
   *  que a rota completa dispara. `loading` NÃO trava a seta (#2330): a leitura
   *  de preço chega a levar 20s, e prender a pessoa nela era o que transformava
   *  uma espera de enfeite em tela travada. */
  const onMonthChange = (next: string) => {
    const idx = months.indexOf(next);
    if (next < month) {
      // `>= 0`, e não `> 0`: o primeiro mês da janela também é página dela —
      // com `> 0` voltar de set/out a ago/set caía no re-âncora (ou em nada,
      // com a janela já no piso) em vez de virar a página.
      if (idx >= 0) {
        setViewIdx(idx);
        return;
      }
      const reach = addMonths(heat.window.fromMonth, -heat.window.count);
      // Meses ISO ("YYYY-MM") comparam cronologicamente como texto; o piso é o mês atual.
      const anchored = reach > monthFloor ? reach : monthFloor;
      if (anchored >= heat.window.fromMonth || !routeOk) return;
      setViewIdx(0);
      preview(origin, destination, anchored);
      return;
    }
    if (idx >= 0 && idx + monthsVisible <= months.length) {
      setViewIdx(idx);
      return;
    }
    if (!routeOk) return;
    setViewIdx(0);
    preview(origin, destination, addMonths(heat.window.fromMonth, heat.window.count));
  };
  return { month, onMonthChange };
}

export function useBinaryPlanner({
  state,
  initial,
  heat,
  heatLoading,
  readOnly,
  autoFetchHeat,
  fetchHeat,
  onSubmit,
  monthsVisible,
  todayIso,
  afterPick,
}: {
  state: PlannerState;
  initial: PlannerFormProps['initial'];
  heat: PlannerHeatView;
  heatLoading: boolean;
  readOnly: boolean;
  autoFetchHeat: boolean;
  fetchHeat: PlannerFormProps['fetchHeat'];
  onSubmit: PlannerFormProps['onSubmit'];
  monthsVisible: number;
  todayIso: string;
  /** Ver `useRoutePreview.afterPick`. */
  afterPick: PlannerGuide['afterRoutePick'];
}) {
  const { origin, destination, travelers, out, setOut, ret, setRet } = state;
  const {
    showTabs,
    oneWayTab,
    routeOk,
    sameEndpoints,
    ready,
    pickingReturn,
    echoPolicyTravelerId,
  } = binaryFlags(state, initial);
  const { analyzed, noHeat, showCalMeta, outTiers, retTiers, pricesLoading } = binaryHeatMeta(
    heat,
    oneWayTab,
    routeOk,
    heatLoading,
    { origin, destination },
  );
  const echoFields = () => ({
    ...(initial.passengers ? { passengers: initial.passengers } : {}),
    ...(travelers.length ? { travelers } : {}),
    ...(echoPolicyTravelerId ? { policyTravelerId: echoPolicyTravelerId } : {}),
  });
  const route = useRoutePreview({
    state,
    heat,
    heatLoading,
    readOnly,
    autoFetchHeat,
    fetchHeat,
    onSubmit,
    routeOk,
    analyzed,
    echoFields,
    afterPick,
  });
  // Escolher a IDA recarrega o heat pedindo TAMBÉM a volta (#2106): `returnDate`
  // presente é o que faz o price-calendar buscar a 2ª perna, e com a janela
  // presente a âncora dela é ignorada — `returnDate = ida` cobre o range inteiro
  // sem campo novo no wire. Só o gesto da IDA paga essa leitura; marcar a volta
  // não re-consulta nada (custo de provider: 2 requests por read com volta).
  const askReturnHeat = (date: string) => {
    route.preview(origin, destination, undefined, undefined, {
      departDate: date,
      returnDate: date,
    });
  };
  const pick = (date: string) => {
    if (oneWayTab) {
      setOut(date);
    } else if (out && ret) {
      setOut(date);
      setRet(undefined);
      askReturnHeat(date);
    } else if (!out || date < out) {
      setOut(date);
      askReturnHeat(date);
    } else {
      setRet(date);
    }
  };
  // Piso do PESCÁVEL: na fase "escolhendo a volta" é a própria ida — INCLUSIVO,
  // porque ir e voltar no mesmo dia é válido (e2e planner.spec). Par completo
  // devolve o piso a hoje: o próximo toque REINICIA o range, e reiniciar em data
  // anterior à ida antiga é legítimo.
  const floorIso = pickingReturn && out ? out : todayIso;
  const dayTone = (date: string) =>
    dayToneOf(tierDayState(date, { out, ret, pickingReturn, outTiers, retTiers }), pricesLoading);
  // Na fase da volta o read da janela leva as MESMAS datas explícitas do pick:
  // pelo closure, `ret` ainda é undefined e a janela re-ancorada voltaria só
  // com o heat da ida — a vista substituída perderia a 2ª perna no meio da
  // escolha da volta.
  const previewWindow = (nextOrigin: string, nextDestination: string, fromMonth?: string) => {
    route.preview(
      nextOrigin,
      nextDestination,
      fromMonth,
      undefined,
      pickingReturn && out ? { departDate: out, returnDate: out } : undefined,
    );
  };
  const pager = useCalendarPager({
    heat,
    routeOk,
    origin,
    destination,
    preview: previewWindow,
    monthsVisible,
    floorIso,
  });
  // A FOLHA (#3446): coluna infinita com o calor de todas as janelas já lidas.
  const sheet = useBinarySheet({
    heat,
    loading: heatLoading,
    origin,
    destination,
    out,
    ret,
    pickingReturn,
    canFetch: routeOk && !heatLoading,
    preview: previewWindow,
  });
  return {
    showTabs,
    oneWayTab,
    pickingReturn,
    floorIso,
    echoFields,
    routeOk,
    sameEndpoints,
    ready,
    out,
    ret,
    sheet,
    analyzed,
    noHeat,
    showCalMeta,
    pick,
    dayTone,
    pickOrigin: route.pickOrigin,
    pickDestination: route.pickDestination,
    swap: route.swap,
    month: pager.month,
    onMonthChange: pager.onMonthChange,
    search: route.search,
  };
}

export type BinaryPlanner = ReturnType<typeof useBinaryPlanner>;
